import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { POSITIONS } from "../../../shared/constants/userConstants";
import { NewUser, validateUser } from "../../../shared/utils/userValidation";
import { profileClient } from "../../../api/clients";
import { useAuthContext } from "../../../context/AuthContext";

export interface AddUserFormProps {
  onUserAdded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  stationId?: string;
}

const initialUser: NewUser = {
  firstName: "",
  lastName: "",
  position: "",
  badgeNumber: "",
  department: "",
  email: "",
};

const AddUserForm: React.FC<AddUserFormProps> = ({
  onUserAdded,
  onError,
  onSuccess,
  stationId,
}) => {
  const theme = useTheme();
  const [newUser, setNewUser] = useState<NewUser>(initialUser);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { organizationId } = useAuthContext();
  const effectiveStationId = stationId ?? organizationId;

  const handleInputChange = (field: keyof NewUser, value: string) => {
    setNewUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddUser = async () => {
    if (!effectiveStationId) {
      onError("Unable to add user: station or organization ID is missing. Please refresh and try again.");
      return;
    }

    const errors = validateUser(newUser);
    if (errors.length > 0) {
      onError(`Validation errors: ${errors.join(", ")}`);
      return;
    }

    setIsSubmitting(true);
    try {
      await profileClient.post(`/v1/stations/${effectiveStationId}/users`, {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        isAdmin,
        metadata: {
          division_name: newUser.department,
          job_title: newUser.position,
          badge_number: newUser.badgeNumber,
        },
        sendOnboardingEmail: true,
      });

      onSuccess("User added successfully!");
      setIsAdmin(false);
      setNewUser(initialUser);
      onUserAdded();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      onError(
        error.response?.data?.error || "Failed to add user. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <PersonAddIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Add Individual User
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create a new user account manually
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            value={newUser.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            value={newUser.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required>
            <InputLabel id="rank-label">Rank</InputLabel>
            <Select
              labelId="rank-label"
              value={newUser.position}
              label="Rank"
              onChange={(e) => handleInputChange("position", e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {POSITIONS.map((rank) => (
                <MenuItem key={rank} value={rank}>
                  {rank}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Badge Number"
            value={newUser.badgeNumber}
            onChange={(e) => handleInputChange("badgeNumber", e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Department"
            value={newUser.department}
            onChange={(e) => handleInputChange("department", e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                color="primary"
              />
            }
            label="Grant Administrator Privileges"
          />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: "auto",
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={isSubmitting || !effectiveStationId}
            startIcon={<PersonAddIcon />}
            sx={{
              width: 200,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(
                  theme.palette.primary.main,
                  0.4
                )}`,
              },
            }}
          >
            {isSubmitting ? "Adding User..." : "Add User"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddUserForm;
